import React, { ReactNode } from 'react';
import classNames from 'classnames';

import './ListRow.scss';

interface ListRowProps {
  /** Primary label rendered at the start of the row. */
  label: ReactNode;
  /** Trailing content shown before the chevron (e.g. a price). */
  trailing?: ReactNode;
  /** `strong` renders a bold, prominent label; `regular` a lighter nested label. */
  emphasis?: 'strong' | 'regular';
  /** Indents the row to signal nesting under a preceding row. */
  indented?: boolean;
  /** Renders the trailing chevron affordance. Defaults to `true`. */
  chevron?: boolean;
  /** Accessible label when the visible `label` is not descriptive on its own. */
  ariaLabel?: string;
  className?: string;
  onClick?(): void;
}

/**
 * Generic clickable list row: a start-aligned label, optional trailing content, and a
 * trailing chevron affordance, with designed hover/focus states. Meant to sit inside a
 * `<ul>` — it renders its own `<li>` wrapper.
 *
 * On hover/focus the row tints and drives three inherited custom properties
 * (`--row-label-color`, `--row-value-color`, `--row-muted-color`) so trailing content
 * rendered by a different component (e.g., a price) can recolor in step without coupling.
 */
export const ListRow = ({
  label,
  trailing,
  emphasis = 'regular',
  indented = false,
  chevron = true,
  ariaLabel,
  onClick,
  className
}: ListRowProps) => (
  <li styleName="list-row__wrap">
    <button
      type="button"
      className={className}
      styleName={classNames('list-row', {
        'list-row--strong': emphasis === 'strong',
        'list-row--regular': emphasis === 'regular',
        'list-row--indented': indented
      })}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      <span styleName="list-row__label">{label}</span>
      {trailing && <span styleName="list-row__trailing">{trailing}</span>}
      {chevron && (
        <span
          className="dashicon dashicon-chevron-right"
          styleName="list-row__chevron"
          aria-hidden="true"
        />
      )}
    </button>
  </li>
);

export default ListRow;
